/**
 * useDraftAutosave: the draft editor's autosave (plan docs/plans/lms-plan.md
 * 8.1 "Autosave every 10 seconds to the draft row (draft content is
 * mutable)"; Appendix A PUT /admin/versions/:id; WP-44).
 *
 * An edit marks its field dirty and (re)starts a short debounce; the save
 * also fires at the latest AUTOSAVE_MAX_WAIT_MS after the first unsaved edit,
 * so a person who never pauses is still saved every ten seconds (8.1). Only
 * the fields that changed travel (the route refuses unknown fields and a
 * standard course's minutes follow the lint's estimate unless the save sets
 * them, 18.17). One save is in flight at a time; edits made meanwhile are
 * saved right after it. The lint comes back with every save and never blocks
 * it (8.1: the lint blocks publish, not a save). A failed save keeps its
 * fields dirty and says so (G10); the next edit or flush() retries.
 *
 * flush() saves now and resolves true when nothing is left unsaved (the
 * editor calls it before Preview, Publish and the lint check); drop() forgets
 * pending changes (a discarded draft). An unmount with unsaved changes fires
 * one last save and does not wait for it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage } from '../../../api/client';
import { saveDraft } from '../../../api/catalog';
import type { DraftPatch, VersionDetail } from '../../../api/catalog';

/** A pause in editing this long saves (8.1 autosave). */
export const AUTOSAVE_DEBOUNCE_MS = 2000;
/** 8.1: "Autosave every 10 seconds": the longest an unsaved edit waits. */
export const AUTOSAVE_MAX_WAIT_MS = 10000;

export type DraftField = keyof DraftPatch;
export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export interface UseDraftAutosaveOptions {
  versionId: string;
  /** Only a draft saves (5.2, G7); a published version's editor is read-only. */
  enabled: boolean;
  /** The current values of every field (read when a save starts, never stale). */
  read: () => Required<DraftPatch>;
  /** Called with the server's answer; fresh is false when newer edits are waiting. */
  onSaved: (detail: VersionDetail, fresh: boolean) => void;
}

export interface UseDraftAutosaveResult {
  state: SaveState;
  error: string | null;
  savedAt: Date | null;
  /** Mark fields changed and schedule the save. */
  schedule: (fields: DraftField[]) => void;
  /** Save now; true when nothing is left unsaved. */
  flush: () => Promise<boolean>;
  /** Forget unsaved changes (the draft was discarded). */
  drop: () => void;
  hasUnsaved: () => boolean;
}

export function useDraftAutosave({ versionId, enabled, read, onSaved }: UseDraftAutosaveOptions): UseDraftAutosaveResult {
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const dirty = useRef<Set<DraftField>>(new Set());
  const seq = useRef(0);
  const debounceTimer = useRef<number | null>(null);
  const maxTimer = useRef<number | null>(null);
  const inflight = useRef<Promise<boolean> | null>(null);
  const again = useRef(false);
  const mounted = useRef(true);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const readRef = useRef(read);
  readRef.current = read;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const saveRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true));

  const clearTimers = useCallback(() => {
    if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
    if (maxTimer.current !== null) window.clearTimeout(maxTimer.current);
    debounceTimer.current = null;
    maxTimer.current = null;
  }, []);

  const save = useCallback((): Promise<boolean> => {
    clearTimers();
    if (inflight.current) {
      again.current = true;
      return inflight.current;
    }
    if (dirty.current.size === 0 || !enabledRef.current) return Promise.resolve(true);

    const fields = Array.from(dirty.current);
    dirty.current = new Set();
    const startSeq = seq.current;
    const values = readRef.current();
    const body: DraftPatch = {};
    fields.forEach((field) => {
      (body as Record<string, unknown>)[field] = values[field];
    });
    if (mounted.current) {
      setState('saving');
      setError(null);
    }

    const run = saveDraft(versionId, body).then(
      (detail) => {
        inflight.current = null;
        if (mounted.current) {
          onSavedRef.current(detail, seq.current === startSeq && dirty.current.size === 0);
          setSavedAt(new Date());
          setState(dirty.current.size > 0 ? 'dirty' : 'saved');
        }
        if (again.current && dirty.current.size > 0) {
          again.current = false;
          return saveRef.current();
        }
        again.current = false;
        return dirty.current.size === 0;
      },
      (err: unknown) => {
        inflight.current = null;
        again.current = false;
        fields.forEach((field) => dirty.current.add(field));
        if (mounted.current) {
          setState('error');
          setError(apiErrorMessage(err, 'The draft could not be saved.'));
        }
        return false;
      }
    );
    inflight.current = run;
    return run;
  }, [versionId, clearTimers]);
  saveRef.current = save;

  const schedule = useCallback(
    (fields: DraftField[]) => {
      if (!enabledRef.current || fields.length === 0) return;
      fields.forEach((field) => dirty.current.add(field));
      seq.current += 1;
      setState('dirty');
      if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
      debounceTimer.current = window.setTimeout(() => {
        debounceTimer.current = null;
        void saveRef.current();
      }, AUTOSAVE_DEBOUNCE_MS);
      if (maxTimer.current === null) {
        maxTimer.current = window.setTimeout(() => {
          maxTimer.current = null;
          void saveRef.current();
        }, AUTOSAVE_MAX_WAIT_MS);
      }
    },
    []
  );

  const flush = useCallback(async (): Promise<boolean> => {
    let ok = await saveRef.current();
    // A save that was in flight when flush() was called: save what came after it.
    if (ok && dirty.current.size > 0) ok = await saveRef.current();
    return ok && dirty.current.size === 0;
  }, []);

  const drop = useCallback(() => {
    clearTimers();
    dirty.current = new Set();
    again.current = false;
    setState('idle');
  }, [clearTimers]);

  const hasUnsaved = useCallback(() => dirty.current.size > 0 || inflight.current !== null, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimers();
      // Leaving the editor with unsaved edits: one last save, not awaited.
      if (dirty.current.size > 0 && !inflight.current && enabledRef.current) void saveRef.current();
    };
  }, [clearTimers]);

  return { state, error, savedAt, schedule, flush, drop, hasUnsaved };
}

export default useDraftAutosave;
